import { useState, useEffect, useCallback } from "react";
import { X, TrendingUp, TrendingDown, Minus, Clock, Target, Zap, BarChart3 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TrendArrow({ current, previous }) {
  if (previous === 0 && current === 0) return <Minus size={12} className="trend-neutral" />;
  if (previous === 0) return <TrendingUp size={12} className="trend-up" />;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct > 0) return <span className="trend-up"><TrendingUp size={12} /> +{pct}%</span>;
  if (pct < 0) return <span className="trend-down"><TrendingDown size={12} /> {pct}%</span>;
  return <Minus size={12} className="trend-neutral" />;
}

function formatHours(h) {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d ${Math.round(h % 24)}h`;
}

export default function ReportQuickView({ open, onClose, onViewFull }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/summary`);
      setData(res.data);
    } catch {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchReport();
  }, [open, fetchReport]);

  if (!open) return null;

  const completionPct = data ? Math.round(data.completion_rate * 100) : 0;

  return (
    <div className="dialog-overlay" onClick={onClose} data-testid="report-quick-overlay">
      <div className="dialog-content report-quick-modal" onClick={(e) => e.stopPropagation()}>
        <div className="report-quick-header">
          <div>
            <div className="dialog-title" style={{ margin: 0 }}>Weekly Report</div>
            {data && (
              <div className="report-quick-period">
                {data.period.start} — {data.period.end}
              </div>
            )}
          </div>
          <button className="bubble-action-btn" onClick={onClose} data-testid="report-quick-close">
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="report-quick-loading">Loading report...</div>
        ) : data ? (
          <>
            <div className="report-stat-grid">
              <div className="report-stat-card" data-testid="stat-completed">
                <div className="report-stat-icon completed"><Target size={16} /></div>
                <div className="report-stat-value">{data.tasks_completed}</div>
                <div className="report-stat-label">Completed</div>
                <div className="report-stat-trend">
                  <TrendArrow current={data.tasks_completed} previous={data.prev_tasks_completed} />
                  <span className="report-stat-prev">vs {data.prev_tasks_completed} prev</span>
                </div>
              </div>

              <div className="report-stat-card" data-testid="stat-created">
                <div className="report-stat-icon created"><Zap size={16} /></div>
                <div className="report-stat-value">{data.tasks_created}</div>
                <div className="report-stat-label">Created</div>
                <div className="report-stat-trend">
                  <TrendArrow current={data.tasks_created} previous={data.prev_tasks_created} />
                  <span className="report-stat-prev">vs {data.prev_tasks_created} prev</span>
                </div>
              </div>

              <div className="report-stat-card" data-testid="stat-completion-rate">
                <div className="report-stat-icon rate"><BarChart3 size={16} /></div>
                <div className="report-stat-value">{completionPct}%</div>
                <div className="report-stat-label">Completion Rate</div>
              </div>

              <div className="report-stat-card" data-testid="stat-avg-time">
                <div className="report-stat-icon time"><Clock size={16} /></div>
                <div className="report-stat-value">{formatHours(data.avg_completion_hours)}</div>
                <div className="report-stat-label">Avg. Completion</div>
              </div>
            </div>

            {/* Mini category breakdown */}
            <div className="report-quick-breakdown">
              <div className="report-quick-breakdown-title">By Category</div>
              <div className="report-mini-bars">
                {["daily", "weekly", "monthly"].map((cat) => {
                  const count = data.by_category[cat] || 0;
                  const max = Math.max(...Object.values(data.by_category), 1);
                  return (
                    <div key={cat} className="report-mini-bar-row">
                      <span className={`report-mini-bar-label meta-badge meta-badge-${cat}`}>{cat}</span>
                      <div className="report-mini-bar-track">
                        <div
                          className={`report-mini-bar-fill report-bar-${cat}`}
                          style={{ width: `${(count / max) * 100}%` }}
                        />
                      </div>
                      <span className="report-mini-bar-count">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              className="dialog-btn dialog-btn-primary report-view-full-btn"
              onClick={onViewFull}
              data-testid="view-full-report-btn"
            >
              View Full Report
            </button>
          </>
        ) : (
          <div className="report-quick-loading">No data available</div>
        )}
      </div>
    </div>
  );
}
